import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, first, map, switchMap } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { Languages } from 'Shared/app/constants/languages.constants';

@Injectable()
export class MultiTranslateHttpLoader implements TranslateLoader {
  readonly #http = inject(HttpClient);

  readonly #formPathSubject = new BehaviorSubject<string | null>(null);
  readonly #formPath$ = this.#formPathSubject.asObservable();

  #supportedLanguages = [Languages.Swedish.resource];

  setFormPath(formPath: string | null) {
    this.#formPathSubject.next(formPath);
  }

  hasFormPath(): boolean {
    return this.#formPathSubject.value !== null;
  }

  setSupportedLanguages(supportedLanguages: string[]) {
    this.#supportedLanguages = supportedLanguages;
  }

  getSupportedLanguages(): string[] {
    return this.#supportedLanguages;
  }

  // Gets translation files for the form and shared resources
  getTranslation(lang: string): Observable<any> {
    return this.#formPath$.pipe(
      first((formPath) => formPath !== null),
      switchMap((formPath) => {
        const urls: Observable<any>[] = [];
        urls.push(this.#http.get(`/assets/locales/_forms/shared/resources/locale/${lang}.json`).pipe(catchError(() => of({}))));

        // Change form language if selected language exists. Fallback to en-US if supported.
        if (this.#supportedLanguages.find((l) => l === lang)) {
          urls.push(
            this.#http
              .get(`/assets/locales/_forms/${formPath}/resources/locale/${lang}.json`)
              .pipe(catchError(() => of({})))
          );
        } else if (this.#supportedLanguages.find((l) => l === Languages.English.resource)) {
          urls.push(
            this.#http
              .get(`/assets/locales/_forms/${formPath}/resources/locale/${Languages.English.resource}.json`)
              .pipe(catchError(() => of({})))
          );
        }

        return forkJoin(urls).pipe(
          map((response) => {
            const keys = response.map((x) => this.getKeys(x));
            return Object.assign({}, ...keys);
          })
        );
      })
    );
  }

  getKeys(data, k = ''): object {
    let keys: Record<string, string> = {};

    for (const i in data) {
      const rest = k.length ? '.' + i : i;

      if (typeof data[i] === 'object') {
        if (!Array.isArray(data[i])) {
          keys = { ...keys, ...this.getKeys(data[i], k + rest) };
        }
      } else {
        keys[k + rest] = data[i];
      }
    }

    return keys;
  }
}
